import React, { useState } from 'react'
import {
	useIonAlert,
} from '@ionic/react'
import { collection, getDocsFromServer, query, where } from 'firebase/firestore'
import LoadingSpinnerButton from './LoadingSpinnerButton'
import { db } from './firebase'
import { deleteUser } from './providers'
import {
	useClub,
	useMyUserTeams,
	useUser,
	useUserHasProPersonally,
} from './data'
import './deleteaccountbutton.css'

async function countManagedTeams(uid: UserId) {
	const q = query(
		collection(db, '/teams'),
		where('managers', 'array-contains', uid),
	)
	const snapshot = await getDocsFromServer(q)
	return snapshot.size
}

const DeleteAccountButton = () => {

    const user = useUser()
    const club = useClub()

	const userHasProPersonally = useUserHasProPersonally()
	const teams = useMyUserTeams()

    const [ presentAlert ] = useIonAlert()

    const [ busy, setBusy ] = useState(false)

	const [ errMsg, setErrMsg ] = useState<string | undefined>()

	type DeleteAccountButtonOnClickProps = {
		okForPro?: boolean,
	}

	const onClick = (opt?: DeleteAccountButtonOnClickProps) => {
		if (!user)
			return

		const { displayName, email, phoneNumber, uid } = user
		const str = displayName || email || phoneNumber || uid

		const header = `Delete Account: ${str}`

		setErrMsg(undefined)

		if (club) {
			presentAlert({
				header,
				message: `You are currently a member of the club "${club.name}." You must leave this club before you can delete your account.`,
				buttons: [ 'OK' ],
			})
			return
		}

		if (teams && Object.keys(teams).length) {
			const teamCount = Object.keys(teams).length
			presentAlert({
				header,
                message: `You have ${teamCount} team(s), which you must remove before you can delete your account. See: Home (top-right) > "..." > "Delete team" (for teams only you manage) / "Stop Coaching" (for shared teams).`,
                buttons: [ 'OK' ],
			})
			return
		}

		if (userHasProPersonally && !opt?.okForPro) {

			presentAlert({
				header,
				message: `WARNING: You have an auto-renewing subscription! You should cancel this before deleting your account.`,
				buttons: [
					{
						text: 'Cancel',
						role: 'cancel',
					},
					{
						text: `Continue anyway`,
						handler: () => {
							setTimeout(() => onClick({ ...opt, okForPro: true }), 500)
						}
					},
				],
			})
			return
		}

        presentAlert({
            header,
			message: `This is PERMANENT and cannot be undone. To continue, type "delete" below`,
			inputs:
				[
					{
						placeholder: 'Type "delete" here',
						attributes: {
							maxlength: 6
						}
					}
				],
			buttons: [
				{
					text: 'Cancel',
					role: 'cancel',
				},
				{
					text: 'Delete Account',
					role: 'destructive',
					handler: onConfirm,
				},
			],
		})
	}

	const onConfirm = async (inputs: any) => {
		console.log('delete account!', user, club, inputs)

		const confirmationString = inputs[0]

		if (confirmationString.localeCompare('delete', 'en', { sensitivity: 'base' })) {
			setErrMsg(`You didn't type "delete" in the text box to confirm.`)
			return true
		}

		if (club) {
			console.log('Club member')
			setErrMsg("Please leave your club first.")
			return true
		}

		if (teams && Object.keys(teams).length) {
			console.log('User still manages teams')
			setErrMsg("Please delete your teams or stop coaching them first.")
			return true
		}

		setBusy(true)

		try {
			let managedTeamCount = 0
			try {
				managedTeamCount = user ? await countManagedTeams(user.uid) : 0
			} catch (err) {
				console.error("Err checking managed teams before account deletion", err)
				setErrMsg("AutoRoster couldn't confirm whether you still manage teams. Please check your connection and try again.")
				return true
			}

			if (managedTeamCount) {
				console.log('User still manages teams according to Firestore', { managedTeamCount })
				setErrMsg("Please delete your teams or stop coaching them first.")
				return true
			}

			console.log('Deleting user!')
			await deleteUser()
		} catch (err) {
			console.error("Err from deleteUser", err)
			setErrMsg(`Internal error: ${err}`)
		} finally {
			setBusy(false)
		}

		return true
	}

	return (
		<div className="delete-account-button">
			{
				errMsg && (
					<p className="warning">
						Failed to delete account: {errMsg}
						<br /><br />
						This can happen if you haven't authenticated recently. Please try logging out and back in.
					</p>
				)
			}
			<LoadingSpinnerButton
				busy={busy}
				onClick={onClick}
				color="danger"
				fill="outline"
				size="small"
			>
				Delete Account
			</LoadingSpinnerButton>
		</div>
	)
}

export default DeleteAccountButton
